import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Workspace,
  UpdateWorkspaceStatsResponse,
  PermanentlyDeleteWorkspaceResponse,
  SoftDeleteWorkspaceResponse,
  WorkspaceDeletionImpact
} from '@core/services/auth/workspace.service';
import {
  WorkspaceService
} from '@core/services/auth/workspace.service';
import { WorkspaceMembershipModel } from '@shared/definitions/workspace-membership';

/**
 * Phase 4 — signal-based projection of workspace state.
 *
 * This mirrors the pattern used by `AuthStore`. It is kept separate from
 * `WorkspaceService` (which owns Firebase listeners, reinitialization,
 * switch-workspace orchestration, etc.) so that consumers who only need
 * state can depend on the store without pulling the whole service graph.
 *
 * Today the store is populated by `WorkspaceService` (see its `_*Sig`
 * writable signals and public readonly projections). Later sessions can
 * move the state here and leave the service as a pure side-effect
 * layer.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceStore {
  private readonly _currentWorkspaceId = signal<string | null>(null);
  private readonly _currentWorkspace = signal<Workspace | null>(null);
  private readonly _userWorkspaces = signal<readonly Workspace[]>([]);

  private readonly injector = inject(Injector);
  private _service: WorkspaceService | null = null;
  private get service(): WorkspaceService {
    return (this._service ??= this.injector.get(WorkspaceService));
  }

  readonly currentWorkspaceId = this._currentWorkspaceId.asReadonly();
  readonly currentWorkspace = this._currentWorkspace.asReadonly();
  readonly userWorkspaces = this._userWorkspaces.asReadonly();

  /** True once a workspace id is active. */
  readonly hasActiveWorkspace = computed(() => this._currentWorkspaceId() !== null);

  /** Lookup helper — returns the membership object for the current id. */
  readonly currentWorkspaceFromList = computed(() => {
    const id = this._currentWorkspaceId();
    if (!id) return null;
    return this._userWorkspaces().find(w => w.workspaceId === id) ?? null;
  });

  setCurrentWorkspaceId(id: string | null): void {
    this._currentWorkspaceId.set(id);
  }

  /**
   * Publish a workspace id taken from the ID token before any Firestore
   * round-trip (#234). Delegates to the service, which owns the state the rest
   * of the app polls — writing the signal here alone would be overwritten by
   * the service→store bridge and would not release `waitForWorkspace()`.
   */
  adoptWorkspaceIdFromClaims(workspaceId: string): boolean {
    return this.service.adoptWorkspaceIdFromClaims(workspaceId);
  }

  setCurrentWorkspace(ws: Workspace | null): void {
    this._currentWorkspace.set(ws);
  }

  setUserWorkspaces(workspaces: readonly Workspace[]): void {
    this._userWorkspaces.set(workspaces);
  }

  reset(): void {
    this._currentWorkspaceId.set(null);
    this._currentWorkspace.set(null);
    this._userWorkspaces.set([]);
  }

  /** Throws if no workspace is currently active; returns the id otherwise. */
  requireWorkspaceId(): string {
    return this.service.requireWorkspaceId();
  }

  /** True once `WorkspaceService.ensureInitialized` has completed its first pass. */
  isInitialized(): boolean {
    return this.service.isInitialized();
  }

  /** Resolves with the active workspace id, waiting up to `timeout` ms. */
  waitForWorkspace(timeout?: number): Promise<string> {
    return this.service.waitForWorkspace(timeout);
  }

  /** Role check against the active workspace — matches if any supplied role is held. */
  hasRole(roles: string[]): Promise<boolean> {
    return this.service.hasRole(roles);
  }

  /** Switch the active workspace. */
  switchWorkspace(workspaceId: string): Promise<void> {
    return this.service.switchWorkspace(workspaceId);
  }

  /** Clear the active workspace (on sign-out). */
  clearWorkspace(): void {
    this.service.clearWorkspace();
  }

  /** Reload user's workspace memberships (typically after an invitation accept). */
  loadUserWorkspaces(userId: string, retryCount?: number): Promise<void> {
    return this.service.loadUserWorkspaces(userId, retryCount);
  }

  /** Re-run workspace initialization (e.g. after billing change). */
  reinitialize(): Promise<void> {
    return this.service.reinitialize();
  }

  /** Trigger server-side workspace stats recompute. */
  updateWorkspaceStats(workspaceId: string): Observable<UpdateWorkspaceStatsResponse> {
    return this.service.updateWorkspaceStats(workspaceId);
  }

  /** Read-only preview of the third-party impact a deletion would have. */
  getWorkspaceDeletionImpact(workspaceId: string): Promise<WorkspaceDeletionImpact | null> {
    return this.service.getWorkspaceDeletionImpact(workspaceId);
  }

  /** Soft-delete a workspace (30-day recovery window). Confirmation = workspace name. */
  softDeleteWorkspace(workspaceId: string, confirmationName: string): Promise<SoftDeleteWorkspaceResponse> {
    return this.service.softDeleteWorkspace(workspaceId, confirmationName);
  }

  /** Destructive: permanently delete a workspace. Requires `"DELETE"` confirmation. */
  permanentlyDeleteWorkspace(workspaceId: string, confirmationText: string): Promise<PermanentlyDeleteWorkspaceResponse> {
    return this.service.permanentlyDeleteWorkspace(workspaceId, confirmationText);
  }

  /** UI-friendly membership list for the current user. */
  getUserWorkspacesMemberships(): Promise<WorkspaceMembershipModel[]> {
    return this.service.getUserWorkspacesMemberships();
  }
}
